import { useEffect, useState } from "react";
import { FormattedMessage as FM, useIntl } from "react-intl";

import proxy from "api/proxy";
import RecordTag from "./RecordTag";
import {
  PICKER_DELAY,
  PICKER_LIMIT,
  STYLE_FOCUS_RING,
  STYLE_HOTEL_INPUT,
} from "../constants";

function RecordPicker({ model, domain, multiple, value, onChange }) {
  const intl = useIntl();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [failed, setFailed] = useState(false);
  const selected = multiple ? value : value ? [value] : [];
  const selectedIds = selected.map((record) => record.id);
  const text = query.trim();

  useEffect(() => {
    if (!text) return undefined;
    let cancelled = false;
    async function find() {
      const { data, error } = await proxy.search(
        model,
        [["rec_name", "ilike", `%${text}%`], ...domain],
        ["rec_name"],
        PICKER_LIMIT,
      );
      if (cancelled) return;
      setFailed(Boolean(error));
      setResults(data || []);
    }
    const timer = setTimeout(find, PICKER_DELAY);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [model, domain, text]);

  function handleSelect(record) {
    setQuery("");
    setResults([]);
    onChange(multiple ? [...selected, record] : record);
  }

  function handleRemove(record) {
    if (multiple) {
      onChange(selected.filter((item) => item.id !== record.id));
    } else {
      onChange(null);
    }
  }

  const options = results.filter((record) => !selectedIds.includes(record.id));

  return (
    <div className="space-y-2">
      {selected.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {selected.map((record) => (
            <RecordTag key={record.id} record={record} onRemove={handleRemove} />
          ))}
        </ul>
      )}
      <input
        type="search"
        value={query}
        placeholder={intl.formatMessage({ id: "melhous.dash.list.search" })}
        onChange={(event) => setQuery(event.target.value)}
        className={STYLE_HOTEL_INPUT}
      />
      {text && options.length > 0 && (
        <ul className="divide-y divide-neutral-100 rounded-md border border-neutral-200">
          {options.map((record) => (
            <li key={record.id}>
              <button
                type="button"
                onClick={() => handleSelect(record)}
                className={`min-h-11 w-full px-3 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-50 ${STYLE_FOCUS_RING}`}
              >
                {record.rec_name}
              </button>
            </li>
          ))}
        </ul>
      )}
      {text && results.length === PICKER_LIMIT && (
        <p className="text-xs text-neutral-500">
          <FM id="melhous.dash.list.search_more" />
        </p>
      )}
      {failed && (
        <p role="alert" className="text-sm text-red-700">
          <FM id="melhous.dash.list.search_error" />
        </p>
      )}
    </div>
  );
}

export default RecordPicker;
